/**
 * File name validation and sanitization utilities
 * Matches backend Load service validation rules
 * Backend pattern: ^[a-z\d!\-_\.*\'\(\) &]+$ (includes spaces and ampersands)
 * 
 * VALIDATION RULES:
 * 
 * ✅ ALLOWED CHARACTERS (Files & Folders):
 * - Letters: a-z (uppercase auto-converted to lowercase)
 * - Numbers: 0-9
 * - Symbols: - _ . ! ' ( ) * & 
 * - Spaces (including leading and trailing spaces)
 * - Files that only have extensions as their name (.pdf, .gitignore, etc.)
 * 
 * ❌ REJECTED CHARACTERS (Auto-sanitized with warnings):
 * - Special chars: @ # $ % ^ + = [ ] { } ; , < > : " | ? \ / ~ `
 * - Control characters (0x00-0x1F)
 * - Backslashes (\)
 * 
 * ⚠️ AUTO-SANITIZATION (Shows warnings):
 * - Uppercase → lowercase (backend does this automatically)
 * - Remove trailing periods (Windows/S3 compatibility)
 * - Leading period (hidden files like .gitignore) → warn but allow
 * 
 * 📁 FOLDER-SPECIFIC:
 * - Periods in folder names → warn but allow (matches Load Vue behavior)
 * 
 * 📏 PATH LIMITS:
 * - Total path length: 1024 characters (S3 limit)
 * - Individual filename: No limit (only total path matters)
 */

export interface FileNameValidationResult {
  isValid: boolean;
  sanitizedName?: string;
  error?: string;
  warnings?: string[];
}

/**
 * Regex pattern for characters that the backend will strip out during processing
 * Backend allows: a-z, 0-9, -, _, ., !, ', (, ), *, &, and spaces
 * This pattern REJECTS (strips): @ # $ % ^ + = [ ] { } ; , < > : " | ? \ / ~ ` and control chars
 */
const BACKEND_STRIPPED_CHARS = /[^a-zA-Z0-9\-_.*'()!& ]/g;

/**
 * Validate and sanitize a file or folder name
 * @param name - The file or folder name to validate
 * @param isFolder - Whether this is a folder name
 * @returns Validation result with sanitized name if applicable
 */
export function validateFileName(
  name: string,
  isFolder: boolean = false
): FileNameValidationResult {
  const warnings: string[] = [];
  let sanitizedName = name;
  let hasChanges = false;

  // Empty name (checks null/undefined/empty string; whitespace-only handled later)
  if (!name || name.length === 0) {
    return {
      isValid: false,
      error: 'File name cannot be empty'
    };
  }

  // Check for trailing periods (problematic on Windows and S3)
  if (sanitizedName.endsWith('.')) {
    sanitizedName = sanitizedName.replace(/\.+$/, '');
    hasChanges = true;
    warnings.push(`Removed trailing period(s) from ${isFolder ? 'folder' : 'file'} name`);
  }

  // After trimming trailing periods, reject if name becomes empty or only whitespace
  if (!sanitizedName || sanitizedName.trim().length === 0) {
    return {
      isValid: false,
      error: `${isFolder ? 'Folder' : 'File'} name cannot be empty`
    };
  }

  // Check for leading periods (hidden files on Unix)
  if (sanitizedName.startsWith('.') && sanitizedName.length > 1) {
    warnings.push('File name starts with a period (hidden file on Unix systems)');
  }

  // Check for characters that will be stripped by the backend
  const strippedChars = sanitizedName.match(BACKEND_STRIPPED_CHARS);
  if (strippedChars && strippedChars.length > 0) {
    const uniqueChars = [...new Set(strippedChars)].join(', ');
    const cleanedName = sanitizedName.replace(BACKEND_STRIPPED_CHARS, '');
    
    const hasOnlyDots = /^\.+$/.test(cleanedName);
    
    if (!cleanedName || cleanedName.trim().length === 0 || hasOnlyDots) {
      return {
        isValid: false,
        error: `${isFolder ? 'Folder' : 'File'} name consists only of special characters. Please rename the ${isFolder ? 'folder' : 'file'}.`
      };
    }
    
    hasChanges = true;
    sanitizedName = cleanedName;
    warnings.push(`Special characters (${uniqueChars}) will be removed from ${isFolder ? 'folder' : 'file'} name`);
  }

  // Convert to lowercase (backend does this automatically)
  if (sanitizedName !== sanitizedName.toLowerCase()) {
    sanitizedName = sanitizedName.toLowerCase();
    hasChanges = true;
    warnings.push(`${isFolder ? 'Folder' : 'File'} name will be converted to lowercase`);
  }

  // For folders, additional checks
  if (isFolder) {
    // Folders shouldn't have file extensions
    if (sanitizedName.includes('.') && !sanitizedName.startsWith('.')) {
      warnings.push('Folder name contains periods which may be confused with file extensions');
    }
  }

  // Success - return sanitized name if changes were made
  return {
    isValid: true,
    sanitizedName: hasChanges ? sanitizedName : undefined,
    warnings: warnings.length > 0 ? warnings : undefined
  };
}

/**
 * Validate a full file path (including folder structure)
 * @param path - The full path to validate
 * @returns Validation result
 */
export function validateFilePath(path: string): FileNameValidationResult {
  const warnings: string[] = [];
  let sanitizedPath = path;
  let hasChanges = false;

  // Split path into segments
  const segments = path.split('/').filter(Boolean);

  if (segments.length === 0) {
    return {
      isValid: false,
      error: 'Path cannot be empty'
    };
  }

  // Validate each segment
  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    const isLastSegment = i === segments.length - 1;
    const isFolder = !isLastSegment;

    const result = validateFileName(segment, isFolder);

    if (!result.isValid) {
      return {
        isValid: false,
        error: `Invalid ${isFolder ? 'folder' : 'file'} name "${segment}": ${result.error}`
      };
    }

    if (result.sanitizedName) {
      segments[i] = result.sanitizedName;
      hasChanges = true;
    }

    if (result.warnings) {
      warnings.push(...result.warnings.map(w => `${segment}: ${w}`));
    }
  }

  // Rebuild path if changes were made
  if (hasChanges) {
    sanitizedPath = segments.join('/');
  }

  // Check total path length (S3 key limitation)
  const MAX_PATH_LENGTH = 1024;
  if (sanitizedPath.length > MAX_PATH_LENGTH) {
    return {
      isValid: false,
      error: `Full path is too long (${sanitizedPath.length} characters). Maximum length is 1024 characters.`
    };
  }

  return {
    isValid: true,
    sanitizedName: hasChanges ? sanitizedPath : undefined,
    warnings: warnings.length > 0 ? warnings : undefined
  };
}

/**
 * Batch validate multiple files and detect sanitization collisions
 * Automatically appends -1, -2, etc. to resolve naming conflicts
 * @param files - Array of files to validate
 * @returns Map of file paths to validation results (keyed by full relative path to avoid collisions)
 */
export function validateFiles(files: File[]): Map<string, FileNameValidationResult> {
  const results = new Map<string, FileNameValidationResult>();
  const sanitizedPaths = new Map<string, string[]>(); // Track which original paths map to each sanitized path

  // First pass: validate all files and collect sanitized names
  for (const file of files) {
    const path = (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name;
    const result = validateFilePath(path);
    
    // Track sanitized path collisions
    if (result.isValid) {
      const finalPath = result.sanitizedName || path;
      if (!sanitizedPaths.has(finalPath)) {
        sanitizedPaths.set(finalPath, []);
      }
      sanitizedPaths.get(finalPath)!.push(path);
    }
    
    results.set(path, result);
  }

  // Second pass: resolve collisions by appending -1, -2, etc.
  // Pre-seed usedPaths with ALL sanitized paths (including non-colliding ones)
  // to prevent generating a collision-resolved name that conflicts with an existing file
  const usedPaths = new Set<string>();
  for (const sanitizedPath of sanitizedPaths.keys()) {
    usedPaths.add(sanitizedPath);
  }
  
  for (const [sanitizedPath, originalPaths] of sanitizedPaths.entries()) {
    if (originalPaths.length > 1) {
      // Remove the base path temporarily so we can reassign it
      usedPaths.delete(sanitizedPath);
      
      // Multiple files sanitize to the same name - append suffixes to ALL
      for (let i = 0; i < originalPaths.length; i++) {
        const originalPath = originalPaths[i];
        const existingResult = results.get(originalPath)!;
        
        const finalPath = appendSuffixToPath(sanitizedPath, i, usedPaths);
        
        // Create a NEW warnings array (don't mutate the existing one)
        const warnings = existingResult.warnings ? [...existingResult.warnings] : [];
        if (finalPath !== sanitizedPath) {
          warnings.push(`File was renamed to avoid collision with another file (changed from "${sanitizedPath}" to "${finalPath}")`);
        }
        
        results.set(originalPath, {
          isValid: true,
          sanitizedName: finalPath,
          warnings: warnings.length > 0 ? warnings : undefined
        });
        
        usedPaths.add(finalPath);
      }
    }
  }

  return results;
}

/**
 * Append a numeric suffix to a file path before the extension
 * @param path - The file path (may include folder structure)
 * @param index - The collision index (1, 2, 3, etc.)
 * @param usedPaths - Set of already-used paths to check for new collisions
 * @returns Path with suffix appended (e.g., "file.pdf" -> "file-1.pdf")
 */
function appendSuffixToPath(path: string, index: number, usedPaths: Set<string>): string {
  const lastSlashIndex = path.lastIndexOf('/');
  const dirPath = lastSlashIndex >= 0 ? path.substring(0, lastSlashIndex + 1) : '';
  const fileName = lastSlashIndex >= 0 ? path.substring(lastSlashIndex + 1) : path;
  
  const lastDotIndex = fileName.lastIndexOf('.');
  const hasExtension = lastDotIndex > 0;
  
  const baseName = hasExtension ? fileName.substring(0, lastDotIndex) : fileName;
  const extension = hasExtension ? fileName.substring(lastDotIndex) : '';
  
  // Try increasing suffixes starting from index until we find an unused path
  let suffix = index;
  let newPath: string;
  
  do {
    newPath = `${dirPath}${baseName}-${suffix}${extension}`;
    suffix++;
  } while (usedPaths.has(newPath));
  
  return newPath;
}

/**
 * Get a user-friendly error message for validation failures
 */
export function getValidationErrorMessage(results: Map<string, FileNameValidationResult>): string | null {
  const invalid = Array.from(results.entries()).filter(([, result]) => !result.isValid);

  if (invalid.length === 0) {
    return null;
  }

  if (invalid.length === 1) {
    const [fileName, result] = invalid[0];
    return `Cannot upload "${fileName}": ${result.error}`;
  }

  return `Cannot upload ${invalid.length} file(s) due to invalid names:\n${invalid
    .slice(0, 3)
    .map(([name, result]) => `• ${name}: ${result.error}`)
    .join('\n')}${invalid.length > 3 ? `\n... and ${invalid.length - 3} more` : ''}`;
}

/**
 * Get sanitization warnings for files that were modified
 */
export function getSanitizationWarnings(results: Map<string, FileNameValidationResult>): string | null {
  const sanitized = Array.from(results.entries()).filter(
    ([, result]) => result.isValid && result.sanitizedName
  );

  if (sanitized.length === 0) {
    return null;
  }

  if (sanitized.length === 1) {
    const [fileName, result] = sanitized[0];
    return `"${fileName}" was automatically corrected: ${result.warnings?.join(', ')}`;
  }

  return `${sanitized.length} file(s) were automatically corrected to fix naming issues`;
}
